import { AlertTriangle, Camera, Circle, Film, Mic, Quote, Radio, Users, Wrench } from 'lucide-react'
import type { ReactNode } from 'react'

/**
 * Labels and orders for the coverage code vocabularies.
 *
 * Every one of these is a `varchar(32)` code from a `@Converter` enum whose last
 * constant is `UNKNOWN`/`__UNKNOWN__`. A code this build has never heard of is
 * therefore not a bug, it is the forward-compatibility mechanism working — so
 * every lookup falls back to the raw code rather than to "Unknown", which would
 * turn two different unrecognised codes into the same word on screen.
 */

const titleCase = (code: string) =>
  code.charAt(0) + code.slice(1).toLowerCase().replace(/_/g, ' ')

export const EVENT_TYPE_ORDER = [
  'RALLY', 'WALKABOUT', 'TOWN_HALL', 'PRESS_BRIEFING', 'DOOR_TO_DOOR',
  'TALLY_CENTRE', 'FUNDRAISER', 'TRAINING', 'LAUNCH', 'DEBATE', 'OTHER',
] as const

const EVENT_TYPE_LABEL: Record<string, string> = {
  RALLY: 'Rally',
  WALKABOUT: 'Walkabout',
  TOWN_HALL: 'Town hall',
  PRESS_BRIEFING: 'Press briefing',
  DOOR_TO_DOOR: 'Door to door',
  TALLY_CENTRE: 'Tally centre',
  FUNDRAISER: 'Fundraiser',
  TRAINING: 'Training',
  LAUNCH: 'Launch',
  DEBATE: 'Debate',
  OTHER: 'Other',
}
export const eventTypeLabel = (c?: string | null) =>
  !c ? '—' : (EVENT_TYPE_LABEL[c] ?? titleCase(c))

export const EVENT_STATUS_ORDER = ['SCHEDULED', 'LIVE', 'ENDED', 'CANCELLED'] as const
const EVENT_STATUS_LABEL: Record<string, string> = {
  SCHEDULED: 'Scheduled', LIVE: 'Live', ENDED: 'Ended', CANCELLED: 'Cancelled',
}
export const eventStatusLabel = (c?: string | null) =>
  !c ? '—' : (EVENT_STATUS_LABEL[c] ?? titleCase(c))

/**
 * Classification, in the order the backend ranks it — not alphabetical.
 *
 * These are `ClearanceLevel` codes and the ORDER IS THE MEANING: a caller sees an
 * event when their clearance ranks at or above its classification. A picker that
 * sorted these alphabetically (INTERNAL, PUBLIC, RESTRICTED, SECRET, TOP_SECRET)
 * would put the second-least restrictive option first and read as a ladder that
 * is wrong in the middle.
 */
export const CLASSIFICATION_ORDER = [
  'PUBLIC', 'INTERNAL', 'RESTRICTED', 'SECRET', 'TOP_SECRET',
] as const
const CLASSIFICATION_LABEL: Record<string, string> = {
  PUBLIC: 'Public', INTERNAL: 'Internal', RESTRICTED: 'Restricted',
  SECRET: 'Secret', TOP_SECRET: 'Top secret',
}
export const classificationLabel = (c?: string | null) =>
  !c ? '—' : (CLASSIFICATION_LABEL[c] ?? titleCase(c))

/** Anything above INTERNAL gets a visible marker; the rest would be noise on every row. */
export const isSensitive = (c?: string | null) =>
  c === 'RESTRICTED' || c === 'SECRET' || c === 'TOP_SECRET'

export const FEED_STATUS_LABEL: Record<string, string> = {
  OFFLINE: 'Offline', LIVE: 'Live', ENDED: 'Ended', FAILED: 'Failed',
}
export const feedStatusLabel = (c?: string | null) =>
  !c ? '—' : (FEED_STATUS_LABEL[c] ?? titleCase(c))

/**
 * Recording state. `PENDING` is the one worth reading twice: the server has asked
 * the media server to record and has not yet been told whether it started. It is
 * NOT "recording" and it is not a failure, and a UI that collapses it into either
 * is asserting something nobody knows yet.
 */
const RECORDING_STATE_LABEL: Record<string, string> = {
  NONE: 'Not recorded',
  PENDING: 'Starting…',
  RECORDING: 'Recording',
  READY: 'Ready',
  FAILED: 'Failed',
}
export const recordingStateLabel = (c?: string | null) =>
  !c ? '—' : (RECORDING_STATE_LABEL[c] ?? titleCase(c))

const RECORDING_SOURCE_LABEL: Record<string, string> = {
  EGRESS: 'Recorded live',
  OFFLINE_UPLOAD: 'Uploaded from the field',
}
export const recordingSourceLabel = (c?: string | null) =>
  !c ? '—' : (RECORDING_SOURCE_LABEL[c] ?? titleCase(c))

export const ASSIGNMENT_ROLE_ORDER = ['CAMERA', 'PRODUCER', 'OBSERVER'] as const
const ASSIGNMENT_ROLE_LABEL: Record<string, string> = {
  CAMERA: 'Camera', PRODUCER: 'Producer', OBSERVER: 'Observer',
}
export const assignmentRoleLabel = (c?: string | null) =>
  !c ? '—' : (ASSIGNMENT_ROLE_LABEL[c] ?? titleCase(c))

/**
 * Assignment status, said in terms of what it CHANGES.
 *
 * "Awaiting reply" was true and useless: it did not say what the reply decides,
 * so it read as though somebody could not film until they had accepted. They can
 * — accepting does not grant permission to broadcast and declining does not
 * remove it. What accepting does is **hold the camera**: an unclaimed camera on
 * an event with an accepted assignment is refused to anybody else.
 *
 * So the labels name the effect, and `assignmentMeaning` below says the rest in
 * one sentence wherever a status is shown.
 */
const ASSIGNMENT_STATUS_LABEL: Record<string, string> = {
  ASSIGNED: 'Asked — no answer yet',
  ACCEPTED: 'Accepted — camera held',
  DECLINED: 'Declined',
  WITHDRAWN: 'Withdrawn',
  COMPLETED: 'Filmed',
}
export const assignmentStatusLabel = (c?: string | null) =>
  !c ? '—' : (ASSIGNMENT_STATUS_LABEL[c] ?? titleCase(c))

/** The sentence that stops "asked, no answer" reading as "blocked". */
export function assignmentMeaning(code?: string | null): string {
  switch (code) {
    case 'ASSIGNED':
      return 'They have not accepted or declined. They can still film — accepting is not '
        + 'permission, it holds the camera so nobody else claims it.'
    case 'ACCEPTED':
      return 'The camera on this event is held for them; anybody else asking for it is refused.'
    case 'DECLINED':
      return 'They said no. The camera is free for somebody else.'
    case 'WITHDRAWN':
      return 'The assignment was pulled. Kept as a record of who was asked.'
    case 'COMPLETED':
      return 'The event ended and their camera had been on air.'
    default:
      return ''
  }
}

export const MARK_KIND_ORDER = ['MOMENT', 'QUOTE', 'INCIDENT', 'CROWD', 'TECHNICAL'] as const
const MARK_KIND_LABEL: Record<string, string> = {
  MOMENT: 'Moment', QUOTE: 'Quote', INCIDENT: 'Incident',
  CROWD: 'Crowd', TECHNICAL: 'Technical',
}
export const markKindLabel = (c?: string | null) =>
  !c ? '—' : (MARK_KIND_LABEL[c] ?? titleCase(c))

export function markKindIcon(code: string, className = 'h-3.5 w-3.5'): ReactNode {
  switch (code) {
    case 'QUOTE': return <Quote className={className} />
    case 'INCIDENT': return <AlertTriangle className={className} />
    case 'CROWD': return <Users className={className} />
    case 'TECHNICAL': return <Wrench className={className} />
    default: return <Circle className={className} />
  }
}

export const feedKindIcon = (code: string, className = 'h-3.5 w-3.5') =>
  code === 'AUDIO' ? <Mic className={className} /> : <Camera className={className} />

export { Film as ArchiveIcon, Radio as LiveIcon }

/**
 * Where an event is, in one line.
 *
 * `unitLabels` is the resolved chain from the geography port — ward, then
 * constituency, then county. The venue leads because it is what somebody
 * standing there would say, and the chain narrows behind it.
 */
export function placeOf(e: {
  venue?: string | null
  unitName?: string | null
  unitLabels?: string[] | null
} | null | undefined): string {
  if (!e) return '—'
  const parts = [e.venue, ...(e.unitLabels ?? (e.unitName ? [e.unitName] : []))]
  const line = parts.filter(Boolean).join(' · ')
  return line || 'Location not set'
}
